import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Shield, 
  TrendingUp, 
  Users, 
  ArrowLeft, 
  ChevronRight,
  FileCheck,
  Map,
  Clock,
  Car
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import SongonMapTF753 from '../components/SongonMapTF753';
import { useLanguage } from '../context/LanguageContext';

const SongonEastSidePage = () => {
  const { t, language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const scrollToMasterplan = () => {
    document.getElementById('masterplan')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const highlights = [
    { icon: Shield, ...t('eastside.highlights.items')[0] },
    { icon: MapPin, ...t('eastside.highlights.items')[1] },
    { icon: TrendingUp, ...t('eastside.highlights.items')[2] },
    { icon: Users, ...t('eastside.highlights.items')[3] }
  ];

  const details = [
    { icon: Map, label: t('eastside.details.location'), value: t('eastside.details.location_value') },
    { icon: FileCheck, label: t('eastside.details.surface'), value: t('eastside.details.surface_value') },
    { icon: Shield, label: t('eastside.details.status'), value: t('eastside.details.status_value') },
    { icon: Car, label: t('eastside.details.access'), value: t('eastside.details.access_value') }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[80vh] bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920"
            alt="Songon landscape"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 lg:pt-40 lg:pb-32">
          {/* Back Link */}
          <Link 
            to="/"
            className={`inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8 transform transition-all duration-700 ${
              isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}</span>
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Badge */}
              <div className={`transform transition-all duration-700 delay-100 ${
                isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
              }`}>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 backdrop-blur-sm rounded-full border border-amber-500/30 mb-6">
                  <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                  <span className="text-amber-300 text-sm font-medium uppercase tracking-wider">
                    {t('eastside.hero.badge')}
                  </span>
                </span>
              </div>

              {/* Title */}
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight mb-6 transform transition-all duration-700 delay-200 ${
                isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
              }`}>
                {t('eastside.hero.title')}
              </h1>

              {/* Subtitle */}
              <p className={`text-xl lg:text-2xl text-emerald-300 font-light mb-6 transform transition-all duration-700 delay-300 ${
                isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
              }`}>
                {t('eastside.hero.subtitle')}
              </p>

              {/* Description */}
              <p className={`text-lg text-slate-300 leading-relaxed mb-8 max-w-lg transform transition-all duration-700 delay-400 ${
                isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
              }`}>
                {t('eastside.hero.description')}
              </p>

              {/* CTA */}
              <div className={`flex flex-wrap gap-4 transform transition-all duration-700 delay-500 ${
                isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
              }`}>
                <Button 
                  onClick={scrollToMasterplan}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 px-8 py-6 text-base font-medium"
                >
                  {t('eastside.hero.cta')}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline" 
                  className="border-slate-500 text-white hover:bg-slate-800 px-8 py-6 text-base"
                  onClick={scrollToContact}
                >
                  {t('contact.title')}
                </Button>
              </div>
            </div>

            {/* Stats Card */}
            <div className={`hidden lg:block transform transition-all duration-700 delay-600 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`}>
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  {details.map((detail, index) => (
                    <div key={`detail-${detail.label}`} className="text-center p-4">
                      <detail.icon className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                      <p className="text-slate-400 text-sm mb-1">{detail.label}</p>
                      <p className="text-white font-semibold">{detail.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-20 lg:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-gray-900">
              {t('eastside.highlights.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((item, index) => (
              <div 
                key={`highlight-${item.title}`}
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition-shadow border border-stone-100"
              >
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Master Plan Section */}
      <SongonMapTF753 />

      {/* Details Section (Mobile) */}
      <section className="py-16 bg-stone-50 lg:hidden">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-2xl font-semibold text-gray-900 mb-8 text-center">
            {t('eastside.details.title')}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {details.map((detail, index) => (
              <div key={`detail-mob-${detail.label}`} className="bg-white p-4 rounded-xl text-center shadow-sm">
                <detail.icon className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-gray-500 text-xs mb-1">{detail.label}</p>
                <p className="text-gray-900 font-medium text-sm">{detail.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light text-white mb-6">
            {t('eastside.cta.title')}
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            {t('eastside.cta.text')}
          </p>
          <Button 
            onClick={scrollToContact}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 px-10 py-6 text-lg font-medium"
          >
            {t('eastside.cta.button')}
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default SongonEastSidePage;
